import React from 'react';
import { Skeleton, Card, Typography, Stack, Box, Button, Tooltip, Alert } from '@mui/material';
import { AltRoute, Email, QrCode2, Groups } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import ReportsCardHeader from '../cards/ReportsCardHeader';
import ReportsCardContent from '../cards/ReportsCardContent';
import TypedValue from '../../TypedValue';
import CenteredProgress from '../../loadingOverlay/CenteredProgress';
import { lighten } from '@mui/system';
import SurveyTabbedHeatmap from '../surveys/SurveyTabbedHeatmap';
import SurveyDevices from '../surveys/SurveyDevices';
import QuickStats from '../surveys/QuickStats';
import useSnackbar from '../../hooks/useSnackbar';

const Channel = ({ icon, channelText, actionText, figure, unused, onNavigateToChannels, onEditContent }) => {
    const theme = useTheme();

    return (
        <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex' }}>
            <Button
                sx={{
                    borderRadius: 3,
                    flexGrow: 1
                }}
                variant="outlined"
                onClick={unused ? onEditContent : onNavigateToChannels}
            >
                <Stack spacing={2} sx={{ alignItems: 'center', py: 2 }}>
                    {icon}
                    <Typography
                        variant="subtitle1"
                        sx={{
                            textAlign: 'center',
                            lineHeight: 1
                        }}
                    >
                        {channelText}
                    </Typography>
                    <Typography
                        variant="h3"
                        sx={{
                            textAlign: 'center',
                            lineHeight: 0,
                            pt: 3,
                            pb: 2
                        }}
                    >
                        <Tooltip title={figure}>
                            <TypedValue
                                value={figure}
                                type="compactNumber"
                                accuracy={0}
                            />
                        </Tooltip>
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            textAlign: 'center',
                            lineHeight: 1
                        }}
                    >
                        {actionText}
                    </Typography>
                    <Box
                        sx={{
                            color: 'primary',
                            borderRadius: 1,
                            px: 2,
                            py: 0.2,
                            backgroundColor: lighten(theme.palette.primary.main, 0.95)
                        }}
                    >
                        {unused ? 'Configure Channel' : 'See More'}
                    </Box>
                </Stack>
            </Button>
        </Grid>
    );
};

const SurveyChannelsOverview = ({ surveyId, onCopyShareLink, emailsCount, emailSurveyOpens, qrCodesCount, qrCodeSurveyOpens, teamsCampaignsCount, teamsCampaignSurveyOpens, onNavigateToChannels }) => {
    const navigate = useNavigate();

    const emailUnused = Boolean(emailsCount === 0);
    const qrCodesUnused = Boolean(qrCodesCount === 0);
    const teamsUnused = Boolean(teamsCampaignsCount === 0);

    const noChannels = emailUnused && qrCodesUnused && teamsUnused;

    const handleConfigureEmailChannel = () => {
        onCopyShareLink();
        navigate(`/editor/surveys/${surveyId}`);
    };

    const handleConfigureQRChannel = () => {
        onCopyShareLink();
        navigate('/editor', { state: { type: 'qrcode' } });
    };

    const handleConfigureTeamsChannel = () => {
        onCopyShareLink();
        navigate('/editor', { state: { type: 'teams' } });
    };

    return (
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            {noChannels && (
                <Grid size={12}>
                    <Alert
                        severity="info"
                        action={(
                            <Button
                                onClick={onCopyShareLink}
                                size="small"
                            >
                                Copy Share Link
                            </Button>
                        )}
                    >
                        Increase your engagement and reach a wider audience by utilising extra channels
                    </Alert>
                </Grid>
            )}
            <Channel
                icon={(
                    <Email
                        sx={{ fontSize: 48 }}
                        color="primary"
                    />
                )}
                channelText={`${emailsCount} ${emailsCount === 1 ? 'Email' : 'Emails'}`}
                figure={emailSurveyOpens}
                unused={emailUnused}
                onNavigateToChannels={onNavigateToChannels}
                actionText={emailSurveyOpens === 1 ? 'Click' : 'Clicks'}
                onEditContent={handleConfigureEmailChannel}
            />
            <Channel
                icon={(
                    <QrCode2
                        sx={{ fontSize: 48 }}
                        color="primary"
                    />
                )}
                channelText={`${qrCodesCount} ${qrCodesCount === 1 ? 'QR Code' : qrCodesCount === 0 ? 'Active QR Codes' : 'QR Codes'}`}
                figure={qrCodeSurveyOpens}
                unused={qrCodesUnused}
                onNavigateToChannels={onNavigateToChannels}
                actionText={qrCodeSurveyOpens === 1 ? 'Open' : 'Opens'}
                onEditContent={handleConfigureQRChannel}
            />
            <Channel
                icon={(
                    <Groups
                        sx={{ fontSize: 48 }}
                        color="primary"
                    />
                )}
                channelText={`${teamsCampaignsCount} ${teamsCampaignsCount === 1 ? 'Teams Campaign' : 'Teams Campaigns'}`}
                figure={teamsCampaignSurveyOpens}
                unused={teamsUnused}
                onNavigateToChannels={onNavigateToChannels}
                actionText={teamsCampaignSurveyOpens === 1 ? 'Click' : 'Clicks'}
                onEditContent={handleConfigureTeamsChannel}
            />
        </Grid>
    );
};

const SurveyInsights = ({ survey, actions, pages, questions, answers, comments, channels, onSetTab }) => {
    const { showSnackbar } = useSnackbar();

    if (!actions) {
        return (
            <Skeleton />
        );
    }

    const handleCopyShareLink = () => {
        navigator.clipboard.writeText(survey.copylink);
        showSnackbar('Link copied to clipboard', 'success');
    };

    const allDevices = ['Desktop', 'Mobile', 'Tablet'];

    const devices = allDevices.map(device => ({
        name: device,
        value: actions.reduce((acc, item) => item.device === device ? acc + 1 : acc, 0)
    }));

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
                <QuickStats
                    pages={pages}
                    questions={questions}
                    answers={answers}
                    actions={actions}
                    comments={comments}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Card sx={{ height: '100%' }}>
                    <ReportsCardHeader
                        title={`Channels (${+Boolean(survey.emailsCount > 0) + +Boolean(survey.qrCodesCount > 0)})`}
                        icon={<AltRoute />}
                    />
                    <ReportsCardContent paddingBottom={2}>
                        {channels ? (
                            <SurveyChannelsOverview
                                surveyId={survey.id}
                                onCopyShareLink={handleCopyShareLink}
                                emailsCount={survey.emailsCount}
                                emailSurveyOpens={channels.emails.map(e => e.surveyOpens).reduce((acc, cur) => acc + cur, 0)}
                                qrCodesCount={survey.qrCodesCount}
                                qrCodeSurveyOpens={channels.qrCodes.map(e => e.surveyOpens).reduce((acc, cur) => acc + cur, 0)}
                                teamsCampaignsCount={survey.teamsCampaignsCount}
                                teamsCampaignSurveyOpens={channels.teamsCampaigns.map(e => e.surveyOpens).reduce((acc, cur) => acc + cur, 0)}
                                onNavigateToChannels={() => onSetTab('channels')}
                            />
                        ) : (
                            <CenteredProgress colour="primary" />
                        )}
                    </ReportsCardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
                <SurveyTabbedHeatmap actions={actions} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
                <SurveyDevices
                    data={devices}
                    height="100%"
                />
            </Grid>
        </Grid>
    );
};

export default SurveyInsights;